import type { PublicVenue } from './domain'

const weekdayIndexes: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

export function getVenueOpenStatus(venue: Pick<PublicVenue, 'hours' | 'timezone'>, now = new Date()) {
  const hours = venue.hours ?? []
  if (!hours.length) return { isOpen: false, label: 'Hours unavailable' }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: venue.timezone ?? 'UTC',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now)
  const weekday = weekdayIndexes[parts.find((part) => part.type === 'weekday')?.value ?? '']
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0)
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0)
  if (weekday === undefined) return { isOpen: false, label: 'Closed' }
  const currentMinutes = hour * 60 + minute
  const currentWeekMinute = weekday * MINUTES_PER_DAY + currentMinutes
  const openWindow = mergeWeeklyIntervals(hours).find(
    (window) => currentWeekMinute >= window.start && currentWeekMinute < window.end,
  )

  return openWindow
    ? { isOpen: true, label: `Open until ${formatClock(fromMinutes(openWindow.end))}` }
    : { isOpen: false, label: 'Closed' }
}

const MINUTES_PER_DAY = 24 * 60
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY

function mergeWeeklyIntervals(hours: NonNullable<PublicVenue['hours']>) {
  const intervals = hours.flatMap((window) => {
    const start = window.dayOfWeek * MINUTES_PER_DAY + toMinutes(window.startTime)
    let end = window.dayOfWeek * MINUTES_PER_DAY + toMinutes(window.endTime)
    if (end <= start) end += MINUTES_PER_DAY
    return [-MINUTES_PER_WEEK, 0, MINUTES_PER_WEEK].map((offset) => ({
      start: start + offset,
      end: end + offset,
    }))
  }).sort((left, right) => left.start - right.start || left.end - right.end)

  return intervals.reduce<Array<{ start: number; end: number }>>((merged, interval) => {
    const previous = merged.at(-1)
    if (!previous || interval.start > previous.end) merged.push({ ...interval })
    else previous.end = Math.max(previous.end, interval.end)
    return merged
  }, [])
}

function toMinutes(value: string) {
  const [hour, minute] = value.split(':').map(Number)
  return hour * 60 + minute
}

function fromMinutes(value: number) {
  const normalized = ((value % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  const hour = Math.floor(normalized / 60)
  const minute = normalized % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function formatClock(value: string) {
  const [hour, minute] = value.split(':').map(Number)
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: minute ? '2-digit' : undefined,
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2026, 0, 1, hour, minute)))
}
